// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { Navigate, useParams } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useGetActivity, useGetDeal, useGetDealClientSummary, useGetDocuments, useGetTasks, usePatchTask } from "@/hooks/useDeals";
import { useDocumentViewer } from "@/components/deals/documents/DocumentViewer";
import { sideOf } from "@/utils/deals.utils";
import { absoluteUrl } from "@/utils/common.utils";
import { nextClientJourneyItems, recentCompletedItems } from "@/utils/deals-view.utils";
import ClientDealSkeleton from "@/components/deals/skeletons/ClientDealSkeleton";
import EmptyState from "@/components/common/EmptyState";
import DetailShell from "@/components/layout/DetailShell";
import PortalActionCard from "@/components/deals/portal/PortalActionCard";
import PortalContactCard from "@/components/deals/portal/PortalContactCard";
import PortalCurrentChapter from "@/components/deals/portal/PortalCurrentChapter";
import PortalStatusBanner from "@/components/deals/portal/PortalStatusBanner";
import RecentActivityCard from "@/components/deals/portal/RecentActivityCard";
import UpcomingAppointmentsCard from "@/components/deals/portal/UpcomingAppointmentsCard";
import PortalDocumentsCard from "@/components/deals/portal/PortalDocumentsCard";
import { Journey } from "@/components/deals/portal/PortalJourney";
import { AddressBlock } from "./ClientDealEngagement";
import { ContractSheetTrigger } from "./ClientContractSheet";
import { DealAssigneeRole, DealType, ParticipantRole, TaskStatus } from "@/enum/common.enum";
import { SHARED_PROTECTED_ROUTES } from "@/routes/url";

/**
 * ClientDealView — Concierge Letter & Living Journal.
 *
 * Reads top-to-bottom like a letter from the agent, not a project dashboard.
 * Retires the health pill, "am I waiting" widget, and horizontal chip strip.
 * Keeps every capability (checkable client tasks, milestone dates, closing
 * details, direct line to agent) but re-homes them into a warmer surface.
 */
interface ClientDealViewProps {
  dealId?: string | null;
  embedded?: boolean;
}

export default function ClientDealView({ dealId = null, embedded = false }: ClientDealViewProps) {
  const { id: routeDealId } = useParams();
  const id = dealId || routeDealId;
  const { user, context, loading } = useAuth();
  const enabled = Boolean(user) && Boolean(id);
  const { data: deal, isError: dealError, refetch: refetchDeal } = useGetDeal(id, { enabled });
  const { data: summary, isError: summaryError } = useGetDealClientSummary(id, { enabled });
  const { data: tasksData, refetch: refetchTasks } = useGetTasks(id, { enabled });
  const { data: docsData } = useGetDocuments(id, { enabled });
  const { data: activityData } = useGetActivity(id, { enabled });
  const tasks = tasksData?.tasks ?? [];
  const docs = docsData ?? [];
  const activity = activityData ?? [];
  const denied = dealError || summaryError;
  const { mutate: patchTask, mutateAsync: patchTaskAsync } = usePatchTask();
  const { openDocument, viewer: documentViewer } = useDocumentViewer();

  const loadAll = () => { refetchDeal(); refetchTasks(); };

  // Guards
  const myParticipation = (context?.deal_participations || []).find((p) => p.deal_id === id);
  // A membership alone is not grounds to bounce to the internal page. Someone
  // can be an agent in their own brokerage *and* the client on a deal another
  // brokerage runs — `require_deal_access` resolves that correctly and hands
  // back a `client_calm` payload, but the redirect sent them to DealDetail,
  // which asks for `internal_full` and renders a broken page. Their own
  // participation row is the thing that settles it.
  if (!loading && context?.membership && !myParticipation) return <Navigate to={`/deals/${id}`} replace />;
  if (!loading && context && myParticipation && myParticipation.participant_role !== ParticipantRole.CLIENT) {
    return <Navigate to={`/collab/deal/${id}`} replace />;
  }

  if (denied) return <DetailShell main embedded={embedded}><EmptyState icon={ShieldAlert} title="This deal isn’t shared with you" description="Ask your agent to send you a new invitation to this deal." className="mx-auto mt-20 max-w-md" /></DetailShell>;
  if (!deal || !summary) return <DetailShell main embedded={embedded}><ClientDealSkeleton embedded={embedded} /></DetailShell>;

  const dealType = summary?.my_side || sideOf(deal);
  const firstName = (user?.name || "").split(" ")[0] || "there";
  const agent = deal.agent || {};
  // A chosen point of contact (admin, coordinator) carries its own label.
  const agentRoleLabel = agent.role_label
    || (dealType === DealType.SELL ? "Listing Agent" : dealType === DealType.BUY ? "Buyer's Agent" : "Your Agent");
  // Gated to the phase the deal is actually in. Both sides' full checklists are
  // seeded up front, so an un-gated list showed a client their closing-day
  // paperwork while the house was still being photographed — the "overwhelming
  // checklist of the entire deal lifetime" complaint. Tasks carrying no phase
  // are kept rather than hidden: they were added by hand, so somebody meant
  // them for now.
  const phaseTasks = tasks.filter((t) => !t.phase || t.phase === deal.phase);
  const myTasks = phaseTasks.filter((t) => t.assigned_role === DealAssigneeRole.CLIENT);
  // Requirement #9 — the journey mirrors the next unmarked items of the agent's
  // master checklist and advances by itself as the agent works. Fed
  // `phaseTasks`, not `myTasks`: the server already trims a client's feed to
  // `client_visible`, so this list *is* the master checklist minus the rows the
  // agent marked internal. Narrowing it to client-assigned rows turned the
  // journey into a duplicate of the action card above it.
  const journeyUpcoming = nextClientJourneyItems(phaseTasks);
  const journeyCompleted = recentCompletedItems(phaseTasks);

  const completeTask = async (task) => {
    await patchTaskAsync({
      taskId: task.id,
      dealId: task.deal_id,
      payload: { status: TaskStatus.COMPLETE },
    });
    loadAll();
    toast.success("Marked done. Your agent can see this is off your plate.", {
      action: {
        label: "Undo",
        onClick: () => {
          patchTask(
            { taskId: task.id, dealId: task.deal_id, payload: { status: TaskStatus.PENDING } },
            {
              onSuccess: () => {
                loadAll();
                toast.success("Completion undone.");
              },
            },
          );
        },
      },
    });
  };

  return (
    <DetailShell main embedded={embedded}>
      <section className={embedded ? "font-dashboard" : "min-h-screen bg-dash-page font-dashboard"}>
        {/* Landscape: the address and the next-step banner stay full-bleed
            across the top, then the page splits — the journey reads down the
            left, and the two things a client asks between visits ("where are
            we", "who do I call") sit in a rail that follows them down it. */}
        <div className={embedded ? "mx-auto max-w-[1240px] pb-14" : "mx-auto max-w-[1240px] px-4 py-8 sm:px-8 sm:py-10 lg:py-14"}>
          {/* Their own house, when the agent has added a photo. Skipped rather
              than faked when there isn't one. */}
          {deal.hero_image_url && (
            <img
              src={absoluteUrl(deal.hero_image_url)}
              alt={`Photo of ${deal.address}`}
              className="mb-6 h-56 max-h-[40vh] w-full rounded-2xl object-cover sm:h-80"
              data-testid="client-hero-image"
            />
          )}
          <div className="mb-6">
            <AddressBlock deal={deal} dealType={dealType} firstName={firstName} agent={agent} />
          </div>

          <PortalStatusBanner deal={deal} testId="client-status-banner" />

          <PortalActionCard
            tasks={myTasks}
            checklist={phaseTasks}
            team={deal.team || []}
            deal={deal}
            viewerRole="client"
            onComplete={completeTask}
            testId="client-action-items"
          />

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12">
            <div className="min-w-0">
              <Journey
                deal={deal}
                summary={summary}
                upcoming={journeyUpcoming}
                completed={journeyCompleted}
                collapsePast
              />
              <div className="mt-8 space-y-6">
                <UpcomingAppointmentsCard dealId={deal.id} testId="client-appointments" />
                <PortalDocumentsCard docs={docs} onOpen={openDocument} testId="client-documents" />
              </div>

            </div>

            <aside
              className="flex min-w-0 flex-col gap-6 lg:sticky lg:top-5 lg:self-start"
              data-testid="client-rail"
            >
              <PortalCurrentChapter deal={deal} agent={agent} testId="client-current-chapter" />
              <PortalContactCard agent={agent} roleLabel={agentRoleLabel} messageTo={`${SHARED_PROTECTED_ROUTES.CHATS}?deal=${id}`} testId="client-contact-card" />
              {/* 2.4: history is reference, not headline — one click away rather
                  than on the first screen. */}
              {activity.length > 0 && (
                <details className="group" data-testid="client-activity-disclosure">
                  <summary className="cursor-pointer list-none text-[12px] font-bold uppercase tracking-[1.2px] text-dash-muted hover:text-dash-ink">
                    Recent activity
                    <span className="ml-1 font-normal normal-case tracking-normal text-dash-faint group-open:hidden">
                      · {activity.length} update{activity.length === 1 ? "" : "s"}
                    </span>
                  </summary>
                  <div className="mt-3">
                    <RecentActivityCard activity={activity} hostDealType={deal.deal_type} bare />
                  </div>
                </details>
              )}
              {/* Contract reference belongs with the client's other quick-
                  reference cards. The full sheet still opens in place and
                  does not interrupt the journey. */}
              <ContractSheetTrigger deal={deal} side={dealType} docs={docs} onOpenDocument={openDocument} />
            </aside>
          </div>
        </div>
      </section>
      {documentViewer}
    </DetailShell>
  );
}
